import React from "react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  formatEmbedModeLabel,
  getAllowedOriginsValidationError,
  getPurposeOptions,
} from "./publicFormsConfig";

export default function PublicFormPlacementDialog({
  open,
  mode,
  value,
  saving = false,
  onChange,
  onSubmit,
  onClose,
}) {
  const title = mode === "edit" ? "Einsatzort bearbeiten" : "Einsatzort erstellen";
  const purposeOptions = getPurposeOptions(value.purpose_key);
  const allowedOriginsError = getAllowedOriginsValidationError(value.allowed_origins_text || "");

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        onClose();
      }
    }}
    >
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        <div className="max-h-[85vh] overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Einsatzorte beschreiben, wo und mit welchem Zweck ein Formular verwendet wird.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-name">Name</Label>
                <Input
                  id="placement-dialog-name"
                  data-testid="placement-dialog-name"
                  value={value.name || ""}
                  onChange={(event) => onChange({ ...value, name: event.target.value })}
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-purpose-key">Zweck</Label>
                <select
                  id="placement-dialog-purpose-key"
                  data-testid="placement-dialog-purpose-key"
                  className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm"
                  value={value.purpose_key || ""}
                  onChange={(event) => onChange({ ...value, purpose_key: event.target.value })}
                >
                  {purposeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-source-label">Quelle</Label>
                <Input
                  id="placement-dialog-source-label"
                  data-testid="placement-dialog-source-label"
                  value={value.source_label || ""}
                  onChange={(event) => onChange({ ...value, source_label: event.target.value })}
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-cta-label">CTA-Text</Label>
                <Input
                  id="placement-dialog-cta-label"
                  data-testid="placement-dialog-cta-label"
                  value={value.cta_label || ""}
                  onChange={(event) => onChange({ ...value, cta_label: event.target.value })}
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-asset-key">Asset-Schlüssel</Label>
                <Input
                  id="placement-dialog-asset-key"
                  value={value.asset_key || ""}
                  onChange={(event) => onChange({ ...value, asset_key: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Optionaler Schlüssel für Whitepaper, Download oder Angebot.
                </p>
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-campaign-key">Kampagnen-Schlüssel</Label>
                <Input
                  id="placement-dialog-campaign-key"
                  data-testid="placement-dialog-campaign-key"
                  value={value.campaign_key || ""}
                  onChange={(event) => onChange({ ...value, campaign_key: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Optionaler Schlüssel für Kampagnenauswertung.
                </p>
              </div>
              <div className="space-y-2 min-w-0">
                <Label htmlFor="placement-dialog-embed-mode">Einbettungsart</Label>
                <select
                  id="placement-dialog-embed-mode"
                  data-testid="placement-dialog-embed-mode"
                  className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm"
                  value={value.embed_mode || "standalone"}
                  onChange={(event) => onChange({ ...value, embed_mode: event.target.value })}
                >
                  <option value="standalone">{formatEmbedModeLabel("standalone")}</option>
                  <option value="inline">{formatEmbedModeLabel("inline")}</option>
                  <option value="modal">{formatEmbedModeLabel("modal")}</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Steuert, wie der Einsatzort später eingebunden wird.
                </p>
              </div>
              <div className="space-y-2 min-w-0 md:col-span-2">
                <Label htmlFor="placement-dialog-allowed-origins">Erlaubte Website-Origins</Label>
                <Textarea
                  id="placement-dialog-allowed-origins"
                  data-testid="placement-dialog-allowed-origins"
                  value={value.allowed_origins_text || ""}
                  onChange={(event) => onChange({ ...value, allowed_origins_text: event.target.value })}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Trage die Domains ein, auf denen dieses Formular eingebettet werden darf, z. B. https://www.example.de. Eine Origin besteht aus Protokoll, Domain und optional Port - ohne Pfad.
                </p>
                {allowedOriginsError ? (
                  <p className="text-xs text-destructive">{allowedOriginsError}</p>
                ) : null}
              </div>
              <label className="flex min-w-0 items-center gap-2 text-sm md:pt-8">
                <input
                  type="checkbox"
                  checked={Boolean(value.is_active)}
                  onChange={(event) => onChange({ ...value, is_active: event.target.checked })}
                />
                Aktiv
              </label>
              <label className="flex min-w-0 items-start gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  data-testid="placement-dialog-submit-test-mode"
                  checked={Boolean(value.submit_test_mode)}
                  onChange={(event) => onChange({ ...value, submit_test_mode: event.target.checked })}
                />
                <span className="space-y-1">
                  <span className="font-medium">Submit-Testmodus</span>
                  <span className="block text-xs text-muted-foreground">
                    Unterdrückt Mailversand und operative Folgeaufgaben für kontrollierte
                    Test-Submits über diesen Einsatzort. Nur für interne Test-Fixtures
                    verwenden.
                  </span>
                </span>
              </label>
            </div>

            <div className="space-y-2 min-w-0">
              <Label htmlFor="placement-dialog-notes">Notizen</Label>
              <Textarea
                id="placement-dialog-notes"
                value={value.notes || ""}
                onChange={(event) => onChange({ ...value, notes: event.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="button" onClick={onSubmit} disabled={saving || Boolean(allowedOriginsError)}>
                {mode === "edit" ? "Einsatzort speichern" : "Einsatzort anlegen"}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
